import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, ButtonGroup, Menu, MenuDivider, MenuItem, Popover, Spinner, Tooltip } from '@blueprintjs/core'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { OverlayProps } from '../../../helper/configs'
import { hasScrollbarIfHover } from '../../../helper/CSSclasses'
import { Resizable } from 're-resizable'
import { css as css0, cx } from 'emotion'
import { debounce } from 'lodash'
import { globalOnDemandVars } from '../../../helper/context'
import DialogComp from '../../commonInterface/dialog'
import LoadableVisibility from 'react-loadable-visibility/react-loadable'
import ErrorBoundary from '../../commonInterface/errorBoundary'
import { camelCase } from 'voca'

const LoadingForBox = ({ pastDelay, error, timedOut }) => {
  if (error) {
    return <div>error</div>
  } else if (timedOut) {
    return <div>timeOut</div>
  } else if (pastDelay) {
    return <div css={css`width: 100%; height: 300px; margin: 10px auto 0 auto;`} className={'bp3-skeleton'} />
  } else {
    return null
  }
}
const WrapperForBoxContent = LoadableVisibility({
  loader: () => import('../../../helper/WrapperForLoader'),
  loading: LoadingForBox
})

export const dragHandlerHeight = 32
export const dragHandlerHeightForPixel = 38

const DragHandler = ({ title, eventHandlers, currentHeight, dragHandleProps, isDragging, MoreFeatures, boxClass, isFavorite, setDialogOpen }) => {
  const letItBePixel = useMemo(() => (boxClass.letItBePixel), [boxClass.letItBePixel])
  const classes = useMemo(() => ({
    Class1: css`
  ${letItBePixel ? `background: transparent;` : 'background-color: white;'}
  box-shadow: 0 1px 1px rgba(25, 25, 25, 0.15);
  border-top-right-radius: 3px;
  border-top-left-radius: 3px;
  height: ${dragHandlerHeight}px;
  flex: none;
  border-bottom: ${letItBePixel ? 'solid 2px #212529' : 'none'};
  overflow: hidden;
  position: relative;
  `,
    dragHandlerClass: css`
      &:hover {
  cursor: grab;
  }
      &:active {
  cursor: grabbing;
  }
      `,
    titleClass: css`
  user-select: none;
  padding-top: ${letItBePixel ? '0' : '5px'};
  font-size: ${letItBePixel ? '1.5em' : 'inherit'};
  `,
    buttonClass: css`
    background: transparent !important;
    position: absolute;
    top: 2px;
    right: 5px;
    max-width: 90%;
`
  }
  ), [letItBePixel])
  // -----------------------------------------------------------------------------------------------------------
  const [minimized, setMinimized] = useState(currentHeight === (letItBePixel ? dragHandlerHeightForPixel : dragHandlerHeight))
  const unMinimize = useCallback(
    () => {
      eventHandlers.unMinimize()
      setMinimized(false)
    },
    [eventHandlers]
  )
  const minimize = useCallback(
    () => {
      eventHandlers.minimize()
      setMinimized(true)
    },
    [eventHandlers]
  )
  useEffect(() => {
    if (minimized && (currentHeight - (letItBePixel ? dragHandlerHeightForPixel : dragHandlerHeight) > 10)) {
      setMinimized(false)
    }
  }, [currentHeight, letItBePixel, minimized])
  // -----------------------------------------------------------------------------------------------------------
  const favoriteArray = globalOnDemandVars.sidebarValue()['0']
  const [isThisBoxFavorite, setIsThisBoxFavorite] = useState(boxClass.isFavorite)
  const MenuComp = useMemo(() => (
    <Menu>
      {/* <MenuItem icon={minimized ? 'maximize' : 'minimize'} text={minimized ? 'Un-Minimize' : 'Minimize'} onClick={minimized ? unMinimize : minimize} /> */}
      <MenuItem icon={'double-chevron-up'} text={'To top'} onClick={eventHandlers.toTop} />
      <MenuItem icon={'double-chevron-down'} text={'To bottom'} onClick={eventHandlers.toBottom} />
      <MenuDivider />
      { isThisBoxFavorite ? <MenuItem icon={'star'} text={'Remove from favorites'} onClick={() => {
        let thisIndex = favoriteArray.findIndex((anotherBoxClass) => (anotherBoxClass.id === boxClass.id))
        if (process.env.NODE_ENV !== 'production') {
          console.tron.display({
            name: 'appsync / favorite array before',
            preview: boxClass.id,
            value: {
              favoriteArray,
              thisIndex
            }
            // , important: true
          })
        }
        favoriteArray.splice(thisIndex, 1)
        favoriteArray.forEach((boxClass, i) => {
          if (i <= thisIndex) {
            boxClass.decrementFavoriteOrder()
          }
        })
        if (isFavorite) {
          eventHandlers.removeFromFavoriteList(boxClass.id)
        }
        boxClass.setFavorite(false)
        setIsThisBoxFavorite(false)
        if (process.env.NODE_ENV !== 'production') {
          console.tron.display({
            name: 'appsync / favorite array after',
            preview: boxClass.id,
            value: {
              favoriteArray,
              thisIndex
            }
            // , important: true
          })
        }
      }} />
        : <MenuItem icon={'star-empty'} text={'Add to favorites'} onClick={() => {
          favoriteArray.forEach((boxClass) => {
            boxClass.incrementFavoriteOrder()
          })
          favoriteArray.unshift(boxClass)
          boxClass.setFavorite(true)
          setIsThisBoxFavorite(true)
        }} />}
    </Menu>
  ), [boxClass, eventHandlers, favoriteArray, isFavorite, isThisBoxFavorite])
  // -----------------------------------------------------------------------------------------------------------
  const [minimizedByOnMouseDown, setMinimizedByOnMouseDown] = useState(false)
  const onMouseDown = useCallback(
    (e) => {
      dragHandleProps.onMouseDown(e)
      if (!minimized) {
        minimize()
        setMinimizedByOnMouseDown(true)
      }
    },
    [dragHandleProps, minimize, minimized]
  )

  const onMouseUp = useCallback(
    () => {
      setMinimizedByOnMouseDown(false)
    },
    []
  )

  const ref = useRef({ isDragging: false })
  const current = ref.current
  useEffect(() => {
    const prevIsDragging = current.isDragging
    if (prevIsDragging && !isDragging && minimizedByOnMouseDown) {
      unMinimize()
    }
    current.isDragging = isDragging
  }, [current.isDragging, isDragging, minimizedByOnMouseDown, unMinimize])

  // TODO moreFeatures를 받으면 buttonGroup에 Popover가 하나 더 생기고 그 add-on에 specific한걸 할 수 있는거임
  const openDialog = useCallback(
    () => { setDialogOpen(true) },
    [setDialogOpen]
  )
  return (
    <div css={classes.Class1} className={'dragHandler'} title={title}>
      <div css={[classes.titleClass, classes.dragHandlerClass]}
        {...dragHandleProps}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      >
        <span css={css`position: absolute; max-height: 1.5em; overflow: hidden; width: 100%; padding-left: 1.5em;`}>{title}</span>
      </div>
      <ButtonGroup css={classes.buttonClass}>
        <Tooltip {...OverlayProps} position={'top'} content={minimized ? 'Show' : 'Hide'} popoverClassName={`${letItBePixel ? 'pixelFont' : ''} ${boxClass.backgroundColor === 'yellow' ? 'yellowBackground' : (boxClass.backgroundColor === 'red' ? 'redBackground3' : '')}`}>
          <Button minimal icon={minimized ? 'maximize' : 'minimize'} onClick={minimized ? unMinimize : minimize} />
        </Tooltip>
        <Tooltip {...OverlayProps} position={'top'} content={boxClass.hasCustomDialogComponent ? 'Open a Rich Editor' : 'Open in a Big Window'} popoverClassName={`${letItBePixel ? 'pixelFont' : ''} ${boxClass.backgroundColor === 'yellow' ? 'yellowBackground' : (boxClass.backgroundColor === 'red' ? 'redBackground3' : '')}`}>
          <Button minimal icon={boxClass.hasCustomDialogComponent ? 'new-drawing' : 'zoom-in'} onClick={openDialog} />
        </Tooltip>
        <Popover {...OverlayProps} content={MenuComp} minimal>
          <Tooltip {...OverlayProps} position={'top'} content={'Menu'} popoverClassName={`${letItBePixel ? 'pixelFont' : ''} ${boxClass.backgroundColor === 'yellow' ? 'yellowBackground' : (boxClass.backgroundColor === 'red' ? 'redBackground3' : '')}`}>
            <Button minimal icon={'menu'} />
          </Tooltip>
        </Popover>
      </ButtonGroup>
    </div>
  )
}

const Box = ({
  title, eventHandlers, ContentComponent, contentProps, backgroundColor, dragHandleProps, isDragging, initialHeight, setInitialHeight, setNextProps, boxClass, isFavorite
}) => {
  const letItBePixel = useMemo(() => (boxClass.letItBePixel), [boxClass.letItBePixel])
  const cardClass = css`
  ${backgroundColor === 'yellow' ? 'background-color: #f7d51d;' : ''}
  ${backgroundColor === 'red' ? 'background-color: #DB3737;' : ''}
  margin: 0;
  position: absolute;
  // box-shadow: 0 0 0 1px rgba(25, 25, 25, 0.45);
  // box-shadow:0 0 0 1px rgba(16, 22, 26, 0.1), 0 1px 1px rgba(16, 22, 26, 0.2), 0 2px 6px rgba(16, 22, 26, 0.2);
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  ${!backgroundColor ? (`
  &:hover .dragHandler {
    background-color: var(--gray1);
  }
  `) : ''}
  `
  const contentClass = css`
  //background-color: white;
  overflow-y: scroll;
  overflow-x: auto;
  flex: auto;
  `

  const [height, setHeight] = useState(isFavorite ? boxClass.initialHeightForFavorite : initialHeight)
  const updateHeight = useMemo(() => (debounce((isFavorite ? boxClass.setInitialHeightForFavorite : setInitialHeight), 1000, {
    leading: false,
    trailing: true
  })), [boxClass, isFavorite, setInitialHeight])
  const setAndUpdateHeight = useCallback(
    (newHeight) => {
      setHeight(newHeight)
      updateHeight(newHeight)
    }
    ,
    [updateHeight]
  )
  // -----------------------------------------------------------------------------------------------------------
  const ref = useRef({ heightBeforeMinimize: isFavorite ? boxClass.heightBeforeMinimizeForFavorite : boxClass.heightBeforeMinimize })
  const current = ref.current

  // useEffect(() => {
  eventHandlers.minimize = () => {
    if (height - (letItBePixel ? dragHandlerHeightForPixel : dragHandlerHeight) > 10) {
      current.heightBeforeMinimize = height
      boxClass.setHeightBeforeMinimize(height, isFavorite)
    }
    setAndUpdateHeight((letItBePixel ? dragHandlerHeightForPixel : dragHandlerHeight))
  }
  eventHandlers.unMinimize = () => {
    if (height === (letItBePixel ? dragHandlerHeightForPixel : dragHandlerHeight)) {
      setAndUpdateHeight(current.heightBeforeMinimize)
    }
  }
  // }, [current.heightBeforeMinimize, eventHandlers, height])

  const onResizeStop = useCallback(
    (e, direction, ref, d) => {
      // console.tron.display({
      //   name: 'onResizeStop',
      //   value: {
      //     direction,
      //     d,
      //     height
      //   },
      //   preview: height
      //   // , important: true
      // })
      const newHeight = height + d.height
      setAndUpdateHeight(newHeight)
    },
    [height, setAndUpdateHeight]
  )
  const handleStyle = useMemo(() => ({ cursor: 's-resize', width: 'calc(100% - 6px)', left: '3px' }), [])
  const [dialogOpen, setDialogOpen] = useState(false)
  return (<ErrorBoundary>
    <Resizable size={{ width: '100%', height: height }} minHeight={(letItBePixel ? dragHandlerHeightForPixel : dragHandlerHeight)} bounds='parent'
      handleClasses={{ bottom: css0`&:hover{ box-shadow: 0 1px 0 0 var(--primary3); }` }} css={cardClass} className={`${camelCase(title)}` + (letItBePixel ? ' letItBePixel pseudoCard' : '')}
      handleStyles={{ bottom: handleStyle }}
      enable={{
        bottom: true,
        top: false,
        right: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false
      }}
      onResizeStop={onResizeStop}
    >
      <DragHandler title={title} eventHandlers={eventHandlers} currentHeight={height}
        dragHandleProps={dragHandleProps}
        isDragging={isDragging} boxClass={boxClass} isFavorite={isFavorite} setDialogOpen={setDialogOpen}
        letItBePixel={letItBePixel}
      />
      {
        height === (letItBePixel ? dragHandlerHeightForPixel : dragHandlerHeight) ? <div css={css`width: 90%; height: ${current.heightBeforeMinimize - 10}px; margin: 10px auto 0 auto;`} className={'bp3-skeleton'} />
          : <div css={contentClass} className={cx(hasScrollbarIfHover, 'scrollGradient')}>
            <WrapperForBoxContent content={<ContentComponent {...contentProps} setNextProps={setNextProps} />} />
          </div>
      }
    </Resizable>
    {/* height 10짜리 div 넣었는데 이거 없으면 Resizable끼리 겹치는 부분에서 resize 안됨... 그래서 걍 박스 사이 margin 대신 이거 */}
    <div css={css`height: 10px;`} />
    <DialogComp isOpen={dialogOpen} setDialogOpen={setDialogOpen} title={title} type={boxClass.dialogType}>
      <WrapperForBoxContent content={<ContentComponent {...contentProps} setNextProps={setNextProps} forDialog />} />
    </DialogComp>
  </ErrorBoundary>
  )
}

export default Box
